import {
  PriceRange as AtlasPriceRange,
  CasepackLineItem,
  OrderBulkLineItem,
  OrderDiscountLevel,
  OrderDiscountType,
  OrderLineItems,
  OrderPriceRange,
  OrderProductDiscount,
  OrderReviewGigaQueryQuery,
  OrderStatus,
  OrderVariantTotals,
  OrderVariantTotalsConnection,
  SkuLineItem,
  SkuTraitValue,
} from '__generated__/atlas-types'

import { PriceRange } from 'types/orders'
import { decimalWithCommas, formatNameWithCode } from 'utils/formatters'
import {
  fromGlobalId,
  relayConnectionToArray,
} from 'utils/transformations/graphql'

import { PRODUCT_COLOR_CODE, PRODUCT_SIZE_CODE } from 'shop/products/traits'
import { EMPTY_CELL_CONTENT } from 'shop/products/utils'

import {
  Bulk,
  Casepack,
  Color,
  Price,
  Product,
  ProductAmounts,
  Size,
} from '../types'
import { formatPriceRange } from 'features/Orders/Review/utils/review.utils'

/**
 * Formats the summary amounts for the products in the overview order.
 *
 * @param data - The data containing the overview order products.
 * @returns The formatted product amounts.
 */
export function formatSummaryAmounts(
  data: OrderReviewGigaQueryQuery | undefined,
): ProductAmounts {
  return {
    totalUnits: data?.orders.edges?.[0]?.node.products?.totalUnits ?? 0,
    totalProducts: data?.orders.edges?.[0]?.node.products?.totalProducts ?? 0,
    totalVariant: data?.orders.edges?.[0]?.node.products?.totalVariant ?? 0,
  }
}

/**
 * Formats the products data into a standardized format.
 *
 * @param data - The data containing the products information.
 * @param currencyCode - The currency code to be used for formatting wholesale price.
 * @param retailCurrencyCode - The currency code to be used for formatting retail prices.
 * @returns An array of formatted Product objects.
 */
export function formatProducts({
  data,
  currencyCode,
  retailCurrencyCode,
}: {
  data: OrderReviewGigaQueryQuery | undefined
  currencyCode: string
  retailCurrencyCode: string
}): Product[] {
  const order = data?.orders.edges?.[0]?.node
  const iShippedOrApprovedOrder =
    order?.status === OrderStatus.SHIPPED ||
    order?.status === OrderStatus.APPROVED
  const displayCountryOfOrigin =
    !!data?.orders.edges?.[0]?.node?.brand?.orderSettings
      ?.displayCountryOfOrigin
  const brandId = data?.orders.edges?.[0]?.node?.brand?.id

  return (
    order?.products?.edges?.map(
      ({
        node,
        variantTotals,
        priceRange,
        wholesaleRangeOverwritten,
        orderLineItems,
        backupProduct,
        orderProductComment,
        quantity,
        discounts,
        attributes,
        collectionProduct,
        financials,
      }) => {
        const wholesalePrice = formatWholesalePrice(
          priceRange,
          wholesaleRangeOverwritten,
          currencyCode,
        )

        const wholesaleTotal = financials?.wholesaleTotal
        const subTotal = financials?.subTotal

        const hasSkuPricing =
          wholesalePrice.range?.maxPrice !== wholesalePrice.range?.minPrice

        return {
          id: node.id,
          name:
            iShippedOrApprovedOrder && backupProduct
              ? backupProduct.productName
              : node.name,
          externalIdentifier:
            iShippedOrApprovedOrder && backupProduct
              ? backupProduct.productNumber
              : node.externalIdentifier,
          imageUrl: node.mainImage?.url ?? '',
          currencyCode,
          quantity: quantity ?? 0,
          wholesalePrice,
          retailPrice: formatRetailPrice(priceRange, retailCurrencyCode),
          colors: formatProductColors(
            variantTotals as OrderVariantTotalsConnection,
            orderLineItems as OrderLineItems,
            currencyCode,
            hasSkuPricing,
          ),
          productComment: orderProductComment ?? '',
          totalStyleCost: formatCurrencyPriceRangeStr(
            currencyCode,
            wholesaleTotal,
          ),
          ...(discounts?.length > 0 && {
            styleDiscount: formatProductDiscount(
              subTotal as OrderPriceRange,
              wholesaleTotal as OrderPriceRange,
              discounts as OrderProductDiscount[],
              currencyCode,
            ),
          }),
          ...(discounts?.length > 0 && {
            finalStyleCost: formatCurrencyPriceRangeStr(currencyCode, subTotal),
          }),
          attributes: attributes?.length ? attributes : [],
          countryOfOrigin: displayCountryOfOrigin
            ? (node.traitValues?.edges?.find(
                (edge) => edge.node.trait.code === 'COUNTRYOFORIGIN',
              )?.node.value ?? '-')
            : undefined,
          collection: collectionProduct?.collection,
          brandId: brandId ?? '',
        }
      },
    ) ?? []
  )
}

/**
 * Formats the discount based on its type, as it could be either a value or percentage.
 *
 * @param OrderProductDiscount[] - List of discounts.
 * @param wholesaleFinalPrice - The final price (with discounts applied).
 * @param wholesaleTotalPrice - The total price (without discounts).
 * @returns The formatted discount.
 */
function formatProductDiscount(
  wholesaleFinalPrice: OrderPriceRange,
  wholesaleTotalPrice: OrderPriceRange,
  discounts: OrderProductDiscount[],
  currencyCode: string,
) {
  const totalMaxDiscount =
    parseFloat(wholesaleTotalPrice.maxPrice) -
    parseFloat(wholesaleFinalPrice.maxPrice)
  const totalMinDiscount =
    parseFloat(wholesaleTotalPrice.minPrice) -
    parseFloat(wholesaleFinalPrice.minPrice)

  const discountValue =
    totalMaxDiscount === totalMinDiscount
      ? `${totalMaxDiscount.toFixed(2)}`
      : `${totalMinDiscount.toFixed(2)} - ${totalMaxDiscount.toFixed(2)}`

  const discount = discounts.find(
    (discount) => discount?.level === OrderDiscountLevel.SKU,
  )
  if (!discount) {
    return
  }
  return discount.type === OrderDiscountType.PERCENT
    ? `${discount.factor}% (${currencyCode} ${discountValue})`
    : `${currencyCode} ${discount.factor}`
}

/**
 * Formats the product colors based on the variant totals connection and currency code.
 *
 * @param variantTotalsConnection - The variant totals connection.
 * @param currencyCode - The currency code.
 * @returns An array of formatted colors.
 */
function formatProductColors(
  variantTotalsConnection: OrderVariantTotalsConnection,
  orderLineItems: OrderLineItems,
  currencyCode: string,
  hasSkuPricing: boolean,
): Color[] {
  if (!variantTotalsConnection?.edges?.length) {
    return []
  }

  const result: Color[] = []

  for (let edge of variantTotalsConnection.edges) {
    if (edge.node) {
      const { skuLineItems, casepackLineItems, bulkLineItems } = orderLineItems
      const params = {
        variantTotals: edge.node,
        skuLineItems: skuLineItems as SkuLineItem[],
        currencyCode,
        casePackLineItems: casepackLineItems as CasepackLineItem[],
        bulkLineItems: bulkLineItems as OrderBulkLineItem[],
        hasSkuPricing,
      }
      result.push(formatColor(params))
    }
  }
  return result
}

function formatColor({
  variantTotals,
  skuLineItems,
  currencyCode,
  casePackLineItems,
  bulkLineItems,
  hasSkuPricing,
}: {
  variantTotals: OrderVariantTotals
  skuLineItems: SkuLineItem[]
  currencyCode: string
  casePackLineItems: CasepackLineItem[]
  bulkLineItems: OrderBulkLineItem[]
  hasSkuPricing: boolean
}): Color {
  return {
    id: variantTotals.variant.id,
    name: formatNameWithCode(
      variantTotals.variant.value,
      variantTotals.variant.externalIdentifier,
    ),
    quantity: variantTotals.quantity,
    swatchImage: variantTotals.variant.displayDetails?.swatchImage?.url ?? '',
    hexColor: variantTotals.variant.displayDetails?.hexColor ?? '',
    currencyCode,
    cost: formatColorCostPrice(variantTotals.wholesaleTotalRange, currencyCode),
    colorComments: variantTotals.variantComment,
    sizes: getSizesByVariantId(variantTotals.variant.id, skuLineItems),
    casepacks: getCasepacksByVariantId(
      variantTotals.variant.id,
      casePackLineItems,
    ),
    bulk: getBulkByVariantId(variantTotals.variant.id, bulkLineItems),
    hasSkuPricing,
    merchandiseImages:
      variantTotals.variant.displayDetails?.merchandiseImages ?? [],
  }
}

/**
 * Retrieves the bulk information for a given variant ID from a list of bulk line items.
 *
 * @param variantId The ID of the variant.
 * @param bulkLineItems The list of bulk line items.
 * @returns The bulk information for the variant ID, or null if not found.
 */
function getBulkByVariantId(
  variantId: string,
  bulkLineItems: OrderBulkLineItem[],
): Bulk | null {
  const foundBulk = bulkLineItems.find(
    (lineItem) => lineItem.variant?.id === variantId,
  )
  if (foundBulk) {
    return {
      quantity: foundBulk.quantity ?? 0,
      wholesalePriceRange: foundBulk.wholesalePriceRange,
    }
  }

  return null
}

/**
 * Returns the casepacks for a given variant ID (aka Color).
 *
 * @param variantId - The variant ID.
 * @param casepackLineItems - The casepack line items.
 * @returns An array of casepacks.
 */
function getCasepacksByVariantId(
  variantId: string,
  casepackLineItems: CasepackLineItem[],
): Casepack[] {
  const casepacks: Casepack[] = []

  for (const lineItem of casepackLineItems) {
    const styleColorId = fromGlobalId(variantId).id
    const casepack = lineItem.casepack
    const variantInfo = lineItem.variantInfo

    if (variantInfo?.variantId === styleColorId) {
      const totalSizedUnits = casepack.skus.edges.reduce(
        (acc, { quantity }) => acc + quantity,
        0,
      )

      const sizes: Size[] = []

      for (const sku of casepack.skus.edges) {
        const skuTraitsValues: SkuTraitValue[] = relayConnectionToArray(
          sku.node.variantValues,
        )
        const foundColorTraitValue = skuTraitsValues.find(
          (value) =>
            value.trait.code === PRODUCT_COLOR_CODE && value.id === variantId,
        )
        const foundSizeTraitValue = skuTraitsValues.find(
          (value) => value.trait.code === PRODUCT_SIZE_CODE,
        )

        if (foundColorTraitValue && foundSizeTraitValue) {
          sizes.push({
            label: foundSizeTraitValue.value.trim(),
            quantity: sku.quantity,
            orderNbr: foundSizeTraitValue.orderNbr ?? 0,
          })
        }
      }

      casepacks.push({
        name: variantInfo.sizeName,
        sizes: Array.from(sizes).sort((a, b) => a.orderNbr - b.orderNbr),
        totalSizedUnits,
        quantity: lineItem.quantity,
        wholesalePrice: decimalWithCommas(lineItem.wholesalePrice),
      })
    }
  }

  return casepacks
}

/**
 * Returns the sizes for a given variant ID (aka Color).
 *
 * @param variantId - The variant ID.
 * @param skuLineItems - The SKU line items.
 * @returns An array of sizes.
 */
function getSizesByVariantId(
  variantId: string,
  skuLineItems: SkuLineItem[],
): Size[] {
  const sizes: Size[] = []
  for (let skuLineItem of skuLineItems) {
    const {
      wholesalePrice,
      quantity,
      sku: { variantValues },
    } = skuLineItem

    const skuTraitsValues: SkuTraitValue[] =
      relayConnectionToArray(variantValues)

    const foundColorTraitValue = skuTraitsValues.find(
      (value) =>
        value.trait.code === PRODUCT_COLOR_CODE && value.id === variantId,
    )
    const foundSizeTraitValue = skuTraitsValues.find(
      (value) => value.trait.code === PRODUCT_SIZE_CODE,
    )

    if (foundColorTraitValue && foundSizeTraitValue) {
      sizes.push({
        label: foundSizeTraitValue.value.trim(),
        quantity,
        wholesalePrice: decimalWithCommas(wholesalePrice),
        // This should not be null but by schema it can be
        orderNbr: foundSizeTraitValue.orderNbr ?? 0,
      })
    }
  }

  return Array.from(sizes).sort((a, b) => a.orderNbr - b.orderNbr)
}

/**
 * Formats the wholesale price of a product.
 *
 * @param priceRange - The price range of the product.
 * @param wholesaleRangeOverwritten - The overwritten wholesale price range of the product.
 * @param currencyCode - The currency code used for formatting the price.
 * @returns The formatted wholesale price.
 */
export function formatWholesalePrice(
  priceRange: AtlasPriceRange | null | undefined,
  wholesaleRangeOverwritten: OrderPriceRange | null | undefined,
  currencyCode: string,
): Price {
  const range = formatProductWholesalePriceRange(
    priceRange,
    wholesaleRangeOverwritten,
  )
  return {
    range,
    label: formatCurrencyPriceRangeStr(currencyCode, range, 'WholesaleValue'),
  }
}

/**
 * Formats the retail price range and label.
 * @param priceRange - The price range object.
 * @param currencyCode - The currency code.
 * @returns The formatted price object.
 */
export function formatRetailPrice(
  priceRange: AtlasPriceRange | null | undefined,
  currencyCode: string,
): Price {
  const range = !!priceRange
    ? getPriceRangeByType(priceRange, 'RetailValue')
    : null

  return {
    range,
    label: formatCurrencyPriceRangeStr(currencyCode, range, 'RetailValue'),
  }
}

/**
 * Formats the color cost price.
 *
 * @param priceRange - The price range.
 * @param currencyCode - The currency code.
 * @returns The formatted price.
 */
export function formatColorCostPrice(
  priceRange: OrderPriceRange,
  currencyCode: string,
): Price {
  const range = getPriceRangeByType(priceRange)
  return {
    range,
    label: formatCurrencyPriceRangeStr(currencyCode, range),
  }
}

/**
 * Checks if the given range is an OrderPriceRange.
 *
 * @param range - The range to check.
 * @returns True if the range is an OrderPriceRange, false otherwise.
 */
function isOrderPriceRange(
  range: AtlasPriceRange | OrderPriceRange,
): range is OrderPriceRange {
  return (range as OrderPriceRange).minPrice !== undefined
}

/**
 * Returns the price range based on the given range and type.
 *
 * @param range - The price range object.
 * @param type - The type of price range to retrieve. Default is 'WholesaleValue'.
 * @returns The price range with minimum and maximum prices.
 */
function getPriceRangeByType(
  range: AtlasPriceRange | OrderPriceRange,
  type: 'WholesaleValue' | 'RetailValue' = 'WholesaleValue',
): PriceRange {
  if (isOrderPriceRange(range)) {
    return {
      minPrice: range.minPrice,
      maxPrice: range.maxPrice,
    }
  }

  return {
    minPrice: range[`min${type}`].toFixed(2),
    maxPrice: range[`max${type}`].toFixed(2),
  }
}

/**
 * Formats the currency price range string.
 *
 * @param currencyCode - The currency code.
 * @param range - The price range.
 * @param type - The type of price range.
 * @returns The formatted currency price range string.
 */
export function formatCurrencyPriceRangeStr(
  currencyCode: string,
  range: OrderPriceRange | AtlasPriceRange | null | undefined,
  type?: 'WholesaleValue' | 'RetailValue',
) {
  if (!!range) {
    const priceRange = getPriceRangeByType(range, type)
    return `${currencyCode} ${formatPriceRange(priceRange)}`
  }
  return EMPTY_CELL_CONTENT
}

/**
 * Formats the product wholesale price range.
 *
 * If no wholesaleOverwritten is available, it will use the price range to format the wholesale price range.
 *
 * @param priceRange - The AtlasPriceRange object representing the price range.
 * @param wholesaleRangeOverwritten - The OrderPriceRange object representing the overwritten wholesale price range.
 * @returns The formatted PriceRange object or null if no price range is available.
 */
function formatProductWholesalePriceRange(
  priceRange: AtlasPriceRange | null | undefined,
  wholesaleRangeOverwritten: OrderPriceRange | null | undefined,
): PriceRange | null {
  if (
    !!wholesaleRangeOverwritten &&
    wholesaleRangeOverwritten.minPrice !== null &&
    wholesaleRangeOverwritten.maxPrice !== null
  ) {
    return {
      minPrice: wholesaleRangeOverwritten.minPrice,
      maxPrice: wholesaleRangeOverwritten.maxPrice,
    }
  }
  if (!!priceRange) {
    return {
      minPrice: priceRange.minWholesaleValue.toFixed(2),
      maxPrice: priceRange.maxWholesaleValue.toFixed(2),
    }
  }
  return null
}
